/**
 * Measure Sub Tool
 *
 * A hosted `ISubTool` that draws polygons the "old way" — click-to-place points
 * via `MeasureEngine` (AREA mode) — as an alternative to the LineTool's
 * prompt/segmentation engine. Hosts pair it with a kind adapter so the same
 * subtool serves surfaces, rooms, and door/window openings: the drawn polygon
 * is committed through `adapter.commitNew`, and snapped onto the host's base
 * plane via `adapter.snapPolygonToBasePlane`.
 *
 * The subtool only DRAWS. Camera alignment, isolation, and selection tracking
 * stay with the host (a `SurfaceToolBase` / `FloorplanToolBase`), which forwards
 * input here and pushes the base plane via `setBasePlane`.
 */

import * as THREE from 'three';
import { SubToolBase } from '../../SubToolBase';
import { DragEventData } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { MeasureTools } from '../../../utils/Measure';
import { AreaAnnotation, PointoramaAnnotation } from '../../../utils/PointoramaAnnotation';
import { PointoramaPlane } from '../../../utils/PointoramaPlane';
import { MeasureEngine } from '../modules/MeasureEngine';
import { AnnotationKindAdapter } from './line-tool/AnnotationKindAdapter';
import type { EditableShape } from './line-tool/EditableShape';
import type { AnnotationPoint } from '@pointorama/potree-shared';
import type { BasePlaneState } from './line-tool/SelectionContextProvider';

/**
 * Scale every measurement's vertex spheres (1 = full size while an editing
 * tool is active, 0.5 = shrunken elsewhere) and remember the value on the
 * scene so spheres created later (e.g. annotations synced from the server)
 * get the same size. Also used by the LineTool-based hosts whose annotations
 * live in scene.measurements (surfaces, door/window openings).
 */
export function setMeasurementSphereScale(viewer: Viewer, scale: number): void {
  viewer.scene.measurementSphereScale = scale;
  for (const measure of viewer.scene.measurements) {
    if (!measure.spheres) continue;
    for (const sphere of measure.spheres) sphere.setScaleFactor(scale);
  }
}

export class MeasureSubTool extends SubToolBase {
  readonly name = 'measure_draw';

  private engine: MeasureEngine | null = null;
  private shiftKeyPressed = false;
  private currentBasePlane: BasePlaneState | null = null;
  private hostDragging = false;

  /** The host forwards its drag-detection state (used by the engine internally). */
  setHostDragging(dragging: boolean): void {
    this.hostDragging = dragging;
  }

  constructor(
    viewer: Viewer,
    persona: IPersona,
    private readonly kindAdapter: AnnotationKindAdapter,
  ) {
    super(viewer, persona);
    // Own an undo stack so the engine persists edits to existing measurements:
    // its move/add-point dispatch is gated on having an undoManager, and the
    // delete-point path runs through `withUndo`. Without this, edits mutate the
    // annotation visually but never dispatch the `measurement_*` event the
    // server listens to — so they're lost on reload.
    this.initUndoRedo();
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────

  protected onActivate(): void {
    this.engine = new MeasureEngine(this.viewer, MeasureTools.AREA, {
      createAnnotation: () => new AreaAnnotation({ viewer: this.viewer, name: this.viewer.translationService.t('polygon', { swc: true }) }),
      minSpheresToFinish: 3,
      // Scope editing to the kind's own shapes (rooms/openings/areas) — AREA
      // geometry is shared across kinds, ownership is not.
      editableDomain: this.kindAdapter.editDomain,
      undoManager: this.undoRedo ?? undefined,
      isUserDragging: () => this.hostDragging,
      // Route point edits through the kind adapter's commitEdit so each kind
      // lands in its OWN store (room -> getRoomPolygonProperty; surface/opening
      // -> their events) rather than the engine's generic AREA store.
      // `persistPoints` covers undo/redo snapshot restore; the live-edit
      // factories cover the initial edit (both call commitEditByIdentifier).
      // The factories return a no-op event so the generic measurement_*_point
      // handler doesn't also write the (wrong) AREA store.
      persistPoints: (id, _type, points) => this.commitEditByIdentifier(id, points),
      onMovePoint: (measure) => this.editEvent(measure),
      onAddPoint: (measure) => this.editEvent(measure),
      onDeletePoint: (measure) => this.editEvent(measure),
      // Only the hosted kind's own annotations are editable (rooms for the
      // room host, openings for door/window, nothing for structures).
      canEditAnnotation: (annotation) => this.kindAdapter.canEditAnnotation?.(annotation) ?? false,
    });
    this.engine.init();
    this.applyDrawingPlane();
    // engine.init() → setMarkerSizes() already enlarged exactly the owned
    // collection (incl. rooms for the room adapter); no blanket enlarge here.
  }

  /**
   * Persist callback for undo/redo restore (keyed by id, not the live object).
   * Restore only hands us the points, so we must APPLY them to the live
   * annotation first — both to update the on-screen geometry and so the kind
   * adapter's commitEdit (which reads the annotation, e.g. room.getPolygon())
   * sees the restored shape — then commit through the adapter to the correct
   * per-kind store.
   */
  private commitEditByIdentifier(id: string, points: AnnotationPoint[]): void {
    // The edited shape may be a measurement/surface/opening (scene.measurements)
    // or a room (scene.rooms). The adapter's commitEdit applies the polygon to
    // the shape (per-kind) and persists it.
    const shape =
      this.viewer.scene.measurements.find((m) => m.identifier === id) ??
      (this.viewer.scene.rooms as { identifier: string }[]).find((r) => r.identifier === id);
    if (!shape) return;
    const polygon = points.map((p) => new THREE.Vector3(p.position.x, p.position.y, p.position.z));
    this.kindAdapter.commitEdit(this.viewer, shape as unknown as EditableShape, polygon);
  }

  /**
   * Live-edit notification factory: commit via the adapter (the engine has
   * already mutated `measure`) and return a no-op event so the generic
   * `measurement_*_point` handler doesn't also write the wrong AREA store.
   */
  private editEvent(measure: PointoramaAnnotation): { type: string; value: unknown } {
    const polygon = measure.points.map((p) => p.position.clone());
    this.kindAdapter.commitEdit(this.viewer, measure as unknown as EditableShape, polygon);
    return { type: 'measure_subtool_edit_committed', value: { identifier: measure.identifier } };
  }

  protected onDeactivate(): void {
    this.setMeasurementSphereScale(0.5);
    this.engine?.dispose();
    this.engine = null;
    this.viewer.inputHandler.unblockOrbitControls();
  }

  setBasePlane(state: BasePlaneState | null): void {
    this.currentBasePlane = state;
    this.applyDrawingPlane();
  }

  /** True while a polygon is being drawn (used by hosts for Esc/Enter precedence). */
  isDrawingInProgress(): boolean {
    return this.engine?.drawingInProgress() ?? false;
  }

  private applyDrawingPlane(): void {
    if (!this.engine) return;
    // Prefer the plane the host installed on viewerMode.associatedPlane (e.g.
    // the focused WALL plane for door/window) — that's the surface points must
    // land on, and it's what the kind adapter projects onto. Fall back to the
    // selection's base plane (floor for rooms; surface plane for surfaces).
    const associated = this.viewer.viewerMode.associatedPlane;
    if (associated) {
      this.engine.drawingPlane = new PointoramaPlane({
        position: associated.position.clone(),
        normal: associated.getNormal().clone(),
        radius: 1000,
      });
    } else if (this.currentBasePlane) {
      this.engine.drawingPlane = new PointoramaPlane({
        position: this.currentBasePlane.origin.clone(),
        normal: this.currentBasePlane.normal.clone(),
        radius: 1000,
      });
    } else {
      this.engine.drawingPlane = null;
    }
  }

  // ─── Input ──────────────────────────────────────────────────────────

  onMouseMove(event: MouseEvent): void {
    if (!this.engine || this.engine.isDraggingSphere()) return;
    const mouse = this.engine.getMouseFromEvent(event);
    this.engine.updateHoverState();
    this.engine.drawNewMeasure(mouse, this.shiftKeyPressed);
    this.engine.updateVisuals();
  }

  onMouseDown(event: MouseEvent): void {
    if (!this.engine || event.button !== 0) return;
    if (this.engine.shouldBlockOrbitControls()) {
      this.viewer.inputHandler.blockOrbitControls();
    }
  }

  onMouseUp(): void {
    this.viewer.inputHandler.unblockOrbitControls();
  }

  onClick(): void {
    if (!this.engine) return;
    // 3D projection-plane snapping click.
    if (this.engine.planeSnapping.getHoveredPlane()) {
      this.engine.planeSnapping.onClick(THREE.MOUSE.LEFT);
      this.engine.planeSnapping.updateHover();
      return;
    }
    if (!this.engine.canPlacePoint()) return;
    // Closing snap: clicking the first point finishes the polygon.
    if (this.engine.hoveringFirstPoint()) {
      this.finish();
      return;
    }
    this.engine.placePoint();
  }

  onDoubleClick(): void {
    // Double-click finishes the polygon (matches the old manual draw tool).
    this.finish();
  }

  onRightClick(): void {
    if (!this.engine) return;
    // Right-click an existing sphere (not mid-draw): delete that point.
    // Hover is only refreshed on mouse move, so the sphere may already be
    // detached (point removed / spheres rebuilt) and have no parent.
    if (this.engine.hoveredSphere?.parent && !this.engine.drawingInProgress()) {
      const measure = this.engine.hoveredSphere.parent as PointoramaAnnotation;
      const pointIndex = measure.getSphereIndex(this.engine.hoveredSphere);
      this.engine.removeMarker(measure, pointIndex);
      return;
    }
    // Mid-draw: undo the last point, or restart.
    if (this.engine.currentMeasure.spheres.length > 2) {
      this.engine.currentMeasure.removeMarker(this.engine.currentMeasure.spheres.length - 2);
    } else {
      this.engine.startMeasure();
    }
  }

  onKeyDown(event: KeyboardEvent): boolean {
    if (event.key === 'Shift') {
      this.shiftKeyPressed = true;
      return false;
    }
    if (event.key === 'CapsLock') {
      this.shiftKeyPressed = event.getModifierState('CapsLock');
      return false;
    }
    if (event.key === 'Enter') {
      // First Enter finishes the shape and is consumed so a host's window-level
      // Enter handler (e.g. DoorWindowTool's wall-focus exit) doesn't ALSO fire
      // on the same keypress. A later Enter while idle (or a too-small polygon
      // that can't finish) is left for the host to act on (e.g. exit the tool).
      if (this.engine?.drawingInProgress() && this.finish()) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
      return false;
    }
    if (event.key === 'Escape' && this.engine?.drawingInProgress()) {
      // Cancel the in-progress polygon; consume so the host doesn't also act.
      event.preventDefault();
      event.stopPropagation();
      this.engine.startMeasure();
      return true;
    }
    return false;
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Shift') this.shiftKeyPressed = false;
  }

  // ─── Drag (sphere/edge editing of existing measurements) ────────────

  onDragStart(_data: DragEventData): void {
    if (!this.engine) return;
    // If hovering a sphere/edge of an existing measurement, begin dragging it
    // (dragging an edge inserts a new point). Orbit was already blocked on
    // mousedown when shouldBlockOrbitControls() was true.
    if (this.engine.shouldBlockOrbitControls()) {
      this.engine.onFirstDragDetected();
    }
  }

  onDrag(data: DragEventData): void {
    if (!this.engine?.draggedSphere) return;
    this.engine.setHoveredLineIntersect();
    this.engine.setHoveredSphere();
    const mouse = this.engine.getMouseFromEvent(data.event);
    const location = this.engine.getPointUnderMouse(mouse, this.shiftKeyPressed);
    if (location) this.engine.onDragPoint(location);
    this.engine.updateVisuals();
  }

  onDragEnd(_data: DragEventData): void {
    if (!this.engine?.draggedSphere) return;
    this.engine.draggedSphere.unHighlight();
    this.engine.onDragPointEnd();
  }

  // ─── Finish → commit via the kind adapter ───────────────────────────

  /** Finish + commit the current polygon. Returns true if a shape was committed. */
  private finish(): boolean {
    if (!this.engine || !this.currentBasePlane) return false;
    const completed = this.engine.completeCurrentMeasurement();
    if (!completed) return false;

    const rawPoints = completed.points.map((p: { position: THREE.Vector3 }) => p.position.clone());
    if (rawPoints.length < 3) return false;

    const polygon = this.kindAdapter.snapPolygonToBasePlane(rawPoints, {
      viewer: this.viewer,
      basePlane: this.currentBasePlane,
      shape: null,
    });
    this.kindAdapter.commitNew(this.viewer, polygon, { basePlane: this.currentBasePlane });
    return true;
  }

  private setMeasurementSphereScale(scale: number): void {
    setMeasurementSphereScale(this.viewer, scale);
  }
}
